import { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Loader2, Plus, Trash2, Star, Sparkles, Wine, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const LIQUID_UNITS = new Set(["ml", "cl", "dl", "l", "L"]);

/**
 * Inventario Vivo — Fase 3
 * Sección para gestionar los formatos de venta de una bebida (Botella / Copa /
 * Combinado…). El tamaño se almacena en la unidad base del ingrediente.
 */
export function IngredientFormatsSection({ ingredientId, unit, costPerUnit }) {
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [draft, setDraft] = useState({ name: "", size_in_base: "", sale_price: "", is_default: false });

  const load = useCallback(async () => {
    if (!ingredientId) return;
    setLoading(true);
    try {
      const d = await api.getIngredientFormats(ingredientId);
      setFormats(d.formats || []);
    } catch (e) {
      toast.error(e.message || "Error al cargar formatos");
    } finally {
      setLoading(false);
    }
  }, [ingredientId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return toast.error("Nombre obligatorio");
    const size = parseFloat(draft.size_in_base);
    if (!Number.isFinite(size) || size <= 0) return toast.error("Tamaño inválido");
    const price = draft.sale_price === "" ? null : parseFloat(draft.sale_price);
    if (price !== null && (!Number.isFinite(price) || price < 0)) return toast.error("Precio inválido");
    setSaving(true);
    try {
      const created = await api.createIngredientFormat(ingredientId, {
        name: draft.name.trim(),
        size_in_base: size,
        sale_price: price,
        is_default: draft.is_default,
      });
      setFormats((prev) => {
        const updated = draft.is_default ? prev.map((f) => ({ ...f, is_default: false })) : prev;
        return [...updated, created];
      });
      setDraft({ name: "", size_in_base: "", sale_price: "", is_default: false });
      toast.success(`Formato "${created.name}" añadido`);
    } catch (e) {
      toast.error(e.message || "Error al crear formato");
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (fmt) => {
    try {
      await api.updateIngredientFormat(ingredientId, fmt.format_id, {
        name: fmt.name,
        size_in_base: fmt.size_in_base,
        sale_price: fmt.sale_price ?? null,
        is_default: true,
      });
      setFormats((prev) => prev.map((f) => ({ ...f, is_default: f.format_id === fmt.format_id })));
    } catch (e) {
      toast.error(e.message || "Error");
    }
  };

  const handlePriceBlur = async (fmt, newVal) => {
    const price = newVal === "" ? null : parseFloat(newVal);
    if (price !== null && (!Number.isFinite(price) || price < 0)) {
      toast.error("Precio inválido");
      return;
    }
    if ((fmt.sale_price ?? null) === (price ?? null)) return; // no change
    try {
      await api.updateIngredientFormat(ingredientId, fmt.format_id, {
        name: fmt.name,
        size_in_base: fmt.size_in_base,
        sale_price: price,
        is_default: !!fmt.is_default,
      });
      setFormats((prev) => prev.map((f) => f.format_id === fmt.format_id ? { ...f, sale_price: price } : f));
    } catch (e) {
      toast.error(e.message || "Error al guardar precio");
    }
  };

  const handleDelete = async (fmt) => {
    if (!window.confirm(`¿Eliminar el formato "${fmt.name}"?`)) return;
    try {
      await api.deleteIngredientFormat(ingredientId, fmt.format_id);
      setFormats((prev) => {
        const rest = prev.filter((f) => f.format_id !== fmt.format_id);
        if (rest.length && !rest.some((f) => f.is_default)) rest[0].is_default = true;
        return [...rest];
      });
      toast.success("Formato eliminado");
    } catch (e) {
      toast.error(e.message || "Error al eliminar");
    }
  };

  const handleSeedPresets = async () => {
    setSeeding(true);
    try {
      const res = await api.seedIngredientFormatPresets(ingredientId);
      setFormats(res.formats || []);
      toast.success(res.added > 0 ? `${res.added} formato(s) añadidos` : "Presets ya existían");
    } catch (e) {
      toast.error(e.message || "Error al generar presets");
    } finally {
      setSeeding(false);
    }
  };

  const handleSuggestPrice = async (fmt) => {
    try {
      const r = await api.suggestFormatPrice(ingredientId, fmt.format_id);
      await api.updateIngredientFormat(ingredientId, fmt.format_id, {
        name: fmt.name,
        size_in_base: fmt.size_in_base,
        sale_price: r.suggested_price,
        is_default: !!fmt.is_default,
      });
      setFormats((prev) => prev.map((f) => f.format_id === fmt.format_id ? { ...f, sale_price: r.suggested_price } : f));
      toast.success(`Sugerencia: ${r.suggested_price} € (${r.realized_margin_pct}% margen)`, { description: r.rationale });
    } catch (e) {
      toast.error(e.message || "No se pudo sugerir precio");
    }
  };

  const sizeLabel = (size) => `${Number(size).toLocaleString("es-ES", { maximumFractionDigits: 4 })} ${unit}`;

  const costFor = (size) => {
    const c = parseFloat(costPerUnit);
    if (!Number.isFinite(c) || c <= 0) return null;
    return c * Number(size);
  };

  const marginInfo = (fmt) => {
    if (fmt.sale_price == null) return null;
    const cost = costFor(fmt.size_in_base);
    if (cost == null) return null;
    const pct = fmt.sale_price > 0 ? ((fmt.sale_price - cost) / fmt.sale_price) * 100 : 0;
    return { cost, pct };
  };

  const isLiquid = LIQUID_UNITS.has(unit);

  return (
    <div className="space-y-3 border rounded-lg p-3 bg-muted/30" data-testid="ingredient-formats-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Wine className="w-4 h-4 text-primary" />
          Formatos de venta
          {formats.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">{formats.length}</Badge>
          )}
        </div>
        {isLiquid && formats.length === 0 && !loading && (
          <Button
            type="button" variant="outline" size="sm" onClick={handleSeedPresets} disabled={seeding}
            data-testid="seed-format-presets-btn"
          >
            {seeding ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
            Añadir presets (Botella/Copa/Combinado)
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : formats.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2 text-center">
          Aún no hay formatos. Añade uno manualmente{isLiquid ? " o usa los presets" : ""}.
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="ingredient-formats-list">
          {formats.map((fmt) => {
            const mg = marginInfo(fmt);
            return (
              <div
                key={fmt.format_id}
                className="bg-background rounded-md border px-2.5 py-1.5 text-sm space-y-1"
                data-testid={`format-row-${fmt.format_id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium flex-1 truncate">{fmt.name}</span>
                  <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{sizeLabel(fmt.size_in_base)}</span>
                  {fmt.is_default ? (
                    <Badge variant="default" className="text-[10px] gap-1 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/10">
                      <Star className="w-3 h-3 fill-current" /> Default
                    </Badge>
                  ) : (
                    <Button
                      type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                      onClick={() => handleSetDefault(fmt)}
                      data-testid={`set-default-format-${fmt.format_id}`}
                    >
                      <Star className="w-3 h-3 mr-1" /> Marcar
                    </Button>
                  )}
                  <Button
                    type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(fmt)}
                    data-testid={`delete-format-${fmt.format_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 pl-0.5">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">PVP</span>
                  <div className="relative w-24">
                    <Input
                      type="number" min="0" step="0.01"
                      defaultValue={fmt.sale_price ?? ""}
                      key={`price-${fmt.format_id}-${fmt.sale_price ?? ""}`}
                      onBlur={(e) => handlePriceBlur(fmt, e.target.value)}
                      placeholder="—"
                      className="h-7 text-xs pl-5"
                      data-testid={`format-price-${fmt.format_id}`}
                    />
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground pointer-events-none">€</span>
                  </div>
                  <Button
                    type="button" variant="outline" size="sm" className="h-7 px-2 text-[10px] gap-1"
                    onClick={() => handleSuggestPrice(fmt)}
                    disabled={!parseFloat(costPerUnit)}
                    title="Sugerir precio según margen típico del sector"
                    data-testid={`suggest-price-${fmt.format_id}`}
                  >
                    <Wand2 className="w-3 h-3" /> Sugerir
                  </Button>
                  {mg ? (
                    <span className="text-[11px] font-medium tabular-nums whitespace-nowrap">
                      <span className="text-muted-foreground">coste {mg.cost.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € · </span>
                      <span className={mg.pct >= 70 ? "text-emerald-700" : mg.pct >= 50 ? "text-amber-700" : "text-red-700"}>
                        margen {mg.pct.toFixed(1)}%
                      </span>
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">Introduce precio</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t items-end">
        <div className="flex-1 space-y-1 w-full">
          <Label className="text-[11px] text-muted-foreground">Nombre</Label>
          <Input
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Ej: Copa, Combinado…"
            className="h-8 text-sm"
            data-testid="new-format-name"
          />
        </div>
        <div className="w-full sm:w-28 space-y-1">
          <Label className="text-[11px] text-muted-foreground">Tamaño ({unit})</Label>
          <Input
            type="number" min="0" step="0.0001"
            value={draft.size_in_base}
            onChange={(e) => setDraft((d) => ({ ...d, size_in_base: e.target.value }))}
            placeholder="0.15"
            className="h-8 text-sm"
            data-testid="new-format-size"
          />
        </div>
        <div className="w-full sm:w-24 space-y-1">
          <Label className="text-[11px] text-muted-foreground">PVP (€)</Label>
          <Input
            type="number" min="0" step="0.01"
            value={draft.sale_price}
            onChange={(e) => setDraft((d) => ({ ...d, sale_price: e.target.value }))}
            placeholder="6,50"
            className="h-8 text-sm"
            data-testid="new-format-price"
          />
        </div>
        <Button
          type="button" size="sm" onClick={handleAdd} disabled={saving}
          data-testid="add-format-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Añadir</>}
        </Button>
      </div>
    </div>
  );
}

export default IngredientFormatsSection;
